/**
 * DeployToProdCard - "Deploy to Prod" header button + deployment dialog (preview, deploy, rollback).
 * Extracted verbatim from SettingsPage.js. Self-contained feature: owns its own local state/handlers
 * (nothing outside this feature referenced them). Behavior unchanged.
 */
import { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAppMessage } from "../AppMessage";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Loader2, Rocket, History, CheckCircle2, RotateCcw, AlertTriangle, GitCommit, Users } from "lucide-react";

export default function DeployToProdCard({ isTaj }) {
  const msg = useAppMessage();
  const [deployProdConfirmOpen, setDeployProdConfirmOpen] = useState(false);
  const [deployingProd, setDeployingProd] = useState(false);
  const [deployProdSteps, setDeployProdSteps] = useState([]);
  const [deployPreview, setDeployPreview] = useState(null);
  const [loadingPreviewDeploy, setLoadingPreviewDeploy] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  const fetchDeployPreview = async () => {
    setLoadingPreviewDeploy(true);
    try {
      const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod/preview`, { withCredentials: true });
      setDeployPreview(res.data);
    } catch (err) {
      toast.error("Failed to load deploy preview");
    } finally {
      setLoadingPreviewDeploy(false);
    }
  };

  const openDeployDialog = () => {
    setDeployProdSteps([]);
    setDeployProdConfirmOpen(true);
    fetchDeployPreview();
  };

  const handleDeployToProd = async () => {
    setDeployingProd(true);
    setDeployProdSteps([{ message: "Starting production deployment...", status: "info" }]);
    try {
      const res = await api.post(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod`, {}, { withCredentials: true });
      setDeployProdSteps(res.data.steps || []);
      toast.success("Deployed to production successfully");
      fetchDeployPreview();
    } catch (err) {
      const steps = err.response?.data?.steps || [];
      steps.push({ message: err.response?.data?.error || err.message, status: "error" });
      setDeployProdSteps(steps);
      toast.error("Deploy to prod failed");
    } finally {
      setDeployingProd(false);
    }
  };

  const handleRollback = async () => {
    if (!(await msg.confirm('Rollback production?', 'Are you sure you want to rollback production to the previous version?', { confirmLabel: 'Rollback', destructive: true }))) return;
    setRollingBack(true);
    try {
      const res = await api.post(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod/rollback`, {}, { withCredentials: true });
      setDeployProdSteps(res.data.steps || []);
      toast.success("Rolled back to previous version");
      fetchDeployPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <>
        {isTaj && (
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={openDeployDialog}
            disabled={deployingProd}
          >
            {deployingProd ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5" />}
            Deploy to Prod
          </Button>
        )}

      {/* Deploy to Prod Dialog */}
      <Dialog open={deployProdConfirmOpen} onOpenChange={(v) => { if (!deployingProd) { setDeployProdConfirmOpen(v); if (!v) setDeployProdSteps([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Rocket className="h-5 w-5" /> Deploy to Production
            </DialogTitle>
            <DialogDescription>
              Push current dev build to <strong>crm.aptask.com</strong>. Review changes below before deploying.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Recent Deployments */}
          {deployPreview?.recent_deploys?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Recent Deployments</h4>
              {deployPreview.recent_deploys.map((d, i) => (
                <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${d.status === "success" ? "bg-green-50 border-green-200" : d.status === "rollback" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-center gap-2">
                    {d.status === "success" ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : d.status === "rollback" ? <RotateCcw className="h-3.5 w-3.5 text-amber-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                    <div>
                      <span className="font-medium">{d.deployed_by}</span>
                      <span className="text-slate-500 ml-2">{new Date(d.timestamp + "Z").toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                      {d.commit_short && <code className="ml-2 text-xs bg-slate-200 px-1.5 py-0.5 rounded">{d.commit_short}</code>}
                      <Badge variant="outline" className={`ml-2 text-xs ${d.status === "success" ? "text-green-700 border-green-300" : d.status === "rollback" ? "text-amber-700 border-amber-300" : "text-red-700 border-red-300"}`}>{d.status}</Badge>
                    </div>
                  </div>
                  {i === 0 && (
                    <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={handleRollback} disabled={rollingBack || deployingProd}>
                      {rollingBack ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      Rollback
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Changes Preview */}
          {loadingPreviewDeploy ? (
            <div className="flex items-center justify-center py-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading changes...</div>
          ) : deployPreview && deployPreview.total_commits > 0 ? (
            <div className="space-y-3">
              {/* Summary Bar */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <GitCommit className="h-4 w-4 text-blue-600" />
                <span><strong>{deployPreview.total_commits}</strong> commit{deployPreview.total_commits !== 1 ? "s" : ""}</span>
                <span className="text-slate-400">|</span>
                <span><strong>{deployPreview.total_files}</strong> file{deployPreview.total_files !== 1 ? "s" : ""} changed</span>
                <span className="text-slate-400">|</span>
                <span><strong>{deployPreview.authors?.length}</strong> author{deployPreview.authors?.length !== 1 ? "s" : ""}</span>
                {deployPreview.has_backend_changes && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Backend</Badge>}
                {deployPreview.has_frontend_changes && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">Frontend</Badge>}
              </div>

              {/* Commits by Author */}
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Changes by Author</h4>
              {deployPreview.authors?.map((author, ai) => (
                <div key={ai} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-white">{author.name?.[0]?.toUpperCase()}</div>
                      <span className="font-medium text-sm">{author.name}</span>
                      <span className="text-xs text-slate-400">{author.email}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{author.commits.length} commit{author.commits.length !== 1 ? "s" : ""}</Badge>
                  </div>
                  <div className="divide-y">
                    {author.commits.map((c, ci) => (
                      <div key={ci} className="px-3 py-2 text-sm hover:bg-slate-50">
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-slate-400 font-mono">{c.short_hash}</code>
                          <span className="flex-1 truncate">{c.message}</span>
                          <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(c.date).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                        </div>
                        {c.files.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.files.slice(0, 5).map((f, fi) => (
                              <span key={fi} className={`text-xs px-1.5 py-0.5 rounded ${f.status === "A" ? "bg-green-100 text-green-700" : f.status === "D" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                                {f.path.split("/").pop()}
                              </span>
                            ))}
                            {c.files.length > 5 && <span className="text-xs text-slate-400">+{c.files.length - 5} more</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : deployPreview && deployPreview.total_commits === 0 ? (
            <div className="text-center py-6 text-slate-500"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" /> Production is up to date. No new changes to deploy.</div>
          ) : null}

          {/* Deploy Progress */}
          {deployProdSteps.length > 0 && (
            <div className="space-y-1 text-sm font-mono bg-slate-900 text-slate-100 rounded-lg p-3 max-h-48 overflow-y-auto">
              {deployProdSteps.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  {s.status === "ok" ? <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" /> : s.status === "error" ? <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" /> : s.status === "warn" ? <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" /> : <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />}
                  <span>{s.message}</span>
                </div>
              ))}
            </div>
          )}

          </div>
          <DialogFooter className="gap-2 border-t pt-3 mt-2 shrink-0">
            <Button variant="outline" onClick={() => { setDeployProdConfirmOpen(false); setDeployProdSteps([]); }} disabled={deployingProd}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleDeployToProd} disabled={deployingProd || (deployPreview && deployPreview.total_commits === 0)}>
              {deployingProd ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Deploying...</> : <><Rocket className="h-4 w-4 mr-1.5" /> Deploy to Prod</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
